<template>
  <div class="menu-sheet">
    <div class="menu-sheet__backdrop" @click="requestClose" />
    <div class="menu-sheet__column">
      <aside
        ref="panel"
        class="menu-sheet__panel"
        :class="{ 'is-dragging': dragging }"
        :style="panelStyle"
        role="dialog"
        :aria-label="$t('assistantCatHome.openMenu')"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <button
          type="button"
          class="menu-sheet__handle"
          :aria-label="$t('assistantCatHome.closeMenu')"
          @click="onHandleClick"
        />
        <div ref="body" class="menu-sheet__body">
          <div class="menu-sheet__fish">
            <div class="menu-sheet__fish-row">
              <div class="menu-sheet__balance">
                <img src="@/assets/img/icon/fish_icon.png" :alt="$t('purchaseFish.product.fishIcon')" />
                <span>{{ fishLabel }}</span>
              </div>
              <button type="button" class="menu-sheet__charge" @click="$emit('charge')">
                <icon-add />
                <span>{{ $t("assistantCatHome.menu.charge") }}</span>
              </button>
            </div>
            <div class="menu-sheet__fish-meta">
              <button type="button" class="menu-sheet__breakdown" @click="$emit('charge-breakdown')">
                <span>{{ $t("assistantCatHome.menu.chargeBreakdown") }}</span>
                <span class="menu-sheet__meta-amount">{{ chargeLabel }}</span>
                <icon-chevron-right class="menu-sheet__breakdown-chevron" />
              </button>
              <span class="menu-sheet__bonus">
                <span>{{ $t("assistantCatHome.menu.bonus") }}</span>
                <span class="menu-sheet__meta-amount">{{ bonusLabel }}</span>
              </span>
            </div>
          </div>
          <assistant-cat-home-menu-panel
            variant="sheet"
            :writer-type="writerType"
            :feedback-manuscript-mode="feedbackManuscriptMode"
            :generate-summary-mode="generateSummaryMode"
            @action="$emit('action', $event)"
          />
        </div>
      </aside>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import AssistantCatHomeMenuPanel from "@/components/organisms/assistantCatHome/AssistantCatHomeMenuPanel.vue";
import { FeedbackManuscriptMode, GenerateSummaryMode } from "@/lib/models/assist";
import IconAdd from "~icons/figma/Add";
import IconChevronRight from "~icons/figma/ChevronRight";

/** Mobile `kAssistantCatHomeSheetMaxSize` / `MinSize` */
const SHEET_MAX_SIZE = 772 / 852;
const SHEET_MIN_SIZE = 0.5;
const SHEET_SNAP_MS = 180;
const SHEET_DRAG_THRESHOLD_PX = 8;

export default Vue.extend({
  name: "AssistantCatHomeMenuSheet",
  components: {
    IconAdd,
    IconChevronRight,
    AssistantCatHomeMenuPanel,
  },
  props: {
    fishLabel: {
      type: String,
      required: true,
    },
    chargeLabel: {
      type: String,
      required: true,
    },
    bonusLabel: {
      type: String,
      required: true,
    },
    writerType: {
      type: String,
      default: null,
    },
    feedbackManuscriptMode: {
      type: String,
      default: FeedbackManuscriptMode.NORMAL,
    },
    generateSummaryMode: {
      type: String,
      default: GenerateSummaryMode.WAKUWAKU,
    },
  },
  data() {
    return {
      size: SHEET_MAX_SIZE,
      dragging: false,
      closing: false,
      didDrag: false,
      maybeDrag: false,
      fromHandle: false,
      dragStartY: 0,
      dragStartSize: SHEET_MAX_SIZE,
      dragStartScrollTop: 0,
      wheelSnapTimer: null as number | null,
    };
  },
  computed: {
    panelStyle(): Record<string, string> {
      return {
        height: `${this.size * 100}%`,
        transition: this.dragging ? "none" : `height ${SHEET_SNAP_MS}ms ease-out`,
      };
    },
  },
  mounted() {
    const body = this.$refs.body as HTMLElement | undefined;
    if (body) {
      body.addEventListener("wheel", this.onWheel, { passive: false });
    }
    const panel = this.panelEl();
    if (panel) {
      panel.addEventListener("touchstart", this.onTouchStart, { passive: true });
      panel.addEventListener("touchmove", this.onTouchMove, { passive: false });
      panel.addEventListener("touchend", this.onTouchEnd);
      panel.addEventListener("touchcancel", this.onTouchEnd);
    }
  },
  beforeDestroy() {
    const body = this.$refs.body as HTMLElement | undefined;
    if (body) {
      body.removeEventListener("wheel", this.onWheel);
    }
    const panel = this.panelEl();
    if (panel) {
      panel.removeEventListener("touchstart", this.onTouchStart);
      panel.removeEventListener("touchmove", this.onTouchMove);
      panel.removeEventListener("touchend", this.onTouchEnd);
      panel.removeEventListener("touchcancel", this.onTouchEnd);
    }
    if (this.wheelSnapTimer !== null) {
      window.clearTimeout(this.wheelSnapTimer);
    }
  },
  methods: {
    requestClose() {
      if (this.closing) {
        return;
      }
      this.closing = true;
      this.dragging = false;
      this.size = 0;
      window.setTimeout(() => {
        this.$emit("close");
      }, SHEET_SNAP_MS);
    },
    snapOpen() {
      this.size = SHEET_MAX_SIZE;
    },
    settle() {
      if (this.size <= SHEET_MIN_SIZE) {
        this.requestClose();
        return;
      }
      this.snapOpen();
    },
    viewportHeight(): number {
      return this.$el instanceof HTMLElement ? this.$el.clientHeight : window.innerHeight;
    },
    bodyEl(): HTMLElement | null {
      return (this.$refs.body as HTMLElement) || null;
    },
    panelEl(): HTMLElement | null {
      return (this.$refs.panel as HTMLElement) || null;
    },
    beginGesture(clientY: number, target: EventTarget | null) {
      this.fromHandle = Boolean(target instanceof Element && target.closest(".menu-sheet__handle"));
      this.maybeDrag = true;
      this.dragging = false;
      this.didDrag = false;
      this.dragStartY = clientY;
      this.dragStartSize = this.size;
      this.dragStartScrollTop = this.bodyEl()?.scrollTop ?? 0;
    },
    endGesture() {
      if (!this.maybeDrag && !this.dragging) {
        return;
      }
      this.maybeDrag = false;
      if (this.dragging) {
        this.dragging = false;
        this.settle();
      }
    },
    /**
     * App の DraggableScrollableSheet と同じく、1 ジェスチャーで
     * リストスクロール → 先頭でさらに下方向ならシート縮小、を連続させる。
     */
    applyFingerDelta(clientY: number) {
      const dy = clientY - this.dragStartY;
      const body = this.bodyEl();
      const vh = this.viewportHeight() || 1;
      let size = this.dragStartSize;
      let scroll = this.dragStartScrollTop;

      if (dy < 0) {
        const room = (SHEET_MAX_SIZE - size) * vh;
        const up = -dy;
        if (up <= room) {
          size += up / vh;
        } else {
          size = SHEET_MAX_SIZE;
          scroll += up - room;
        }
      } else if (scroll > dy) {
        scroll -= dy;
      } else {
        const extra = dy - scroll;
        scroll = 0;
        size -= extra / vh;
      }

      this.size = Math.min(SHEET_MAX_SIZE, Math.max(0, size));
      if (body) {
        const maxScroll = Math.max(0, body.scrollHeight - body.clientHeight);
        body.scrollTop = Math.min(maxScroll, Math.max(0, scroll));
      }
    },
    onHandleClick() {
      if (this.didDrag) {
        return;
      }
      this.requestClose();
    },
    onPointerDown(event: PointerEvent) {
      if (this.closing || event.button !== 0 || event.pointerType === "touch") {
        return;
      }
      this.beginGesture(event.clientY, event.target);
    },
    onPointerMove(event: PointerEvent) {
      if (event.pointerType === "touch" || !this.maybeDrag || this.closing) {
        return;
      }
      const dy = event.clientY - this.dragStartY;
      if (!this.dragging) {
        if (Math.abs(dy) < SHEET_DRAG_THRESHOLD_PX) {
          return;
        }
        const atTop = !this.bodyEl() || this.bodyEl()!.scrollTop <= 0;
        const pullingUp = dy < 0 && this.size < SHEET_MAX_SIZE;
        if (this.fromHandle || (atTop && dy > 0) || pullingUp) {
          this.dragging = true;
          this.didDrag = true;
          (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        } else {
          this.maybeDrag = false;
        }
        return;
      }
      this.applyFingerDelta(event.clientY);
    },
    onPointerUp(event: PointerEvent) {
      if (event.pointerType === "touch") {
        return;
      }
      this.endGesture();
    },
    onTouchStart(event: TouchEvent) {
      if (this.closing || event.touches.length !== 1) {
        return;
      }
      this.beginGesture(event.touches[0].clientY, event.target);
    },
    onTouchMove(event: TouchEvent) {
      if (!this.maybeDrag || this.closing || event.touches.length !== 1) {
        return;
      }
      const clientY = event.touches[0].clientY;
      const dy = clientY - this.dragStartY;
      if (!this.dragging) {
        if (Math.abs(dy) < SHEET_DRAG_THRESHOLD_PX) {
          return;
        }
        this.dragging = true;
        this.didDrag = true;
      }
      event.preventDefault();
      this.applyFingerDelta(clientY);
    },
    onTouchEnd() {
      this.endGesture();
    },
    onWheel(event: WheelEvent) {
      if (this.closing) {
        return;
      }
      const body = this.bodyEl();
      const atTop = !body || body.scrollTop <= 0;
      // 下方向は本文スクロール。先頭で上方向かつシートが全開でないときだけ拡大する。
      if (!(atTop && event.deltaY < 0 && this.size < SHEET_MAX_SIZE)) {
        return;
      }
      event.preventDefault();
      const next = this.size + Math.abs(event.deltaY) / this.viewportHeight();
      this.size = Math.min(SHEET_MAX_SIZE, next);
      if (this.wheelSnapTimer !== null) {
        window.clearTimeout(this.wheelSnapTimer);
      }
      this.wheelSnapTimer = window.setTimeout(() => {
        this.wheelSnapTimer = null;
        this.settle();
      }, SHEET_SNAP_MS);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.menu-sheet {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  justify-content: center;
}

.menu-sheet__backdrop {
  position: absolute;
  inset: 0;
  background: var(--color-overlay);
}

.menu-sheet__column {
  position: relative;
  width: 100%;
  max-width: 590px;
  height: 100%;
  pointer-events: none;
}

.menu-sheet__panel {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 21;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
  overflow: hidden;
  pointer-events: auto;
  background: var(--color-background-normal);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  box-shadow: var(--shadow-md);
  backdrop-filter: blur(8px);
  touch-action: none;

  &.is-dragging {
    user-select: none;
  }
}

.menu-sheet__handle {
  display: block;
  flex-shrink: 0;
  width: 64px;
  height: 5px;
  margin: 15px auto 0;
  padding: 12px 48px;
  border: none;
  border-radius: 100px;
  background: var(--color-border);
  background-clip: content-box;
  cursor: grab;
  touch-action: none;

  .is-dragging & {
    cursor: grabbing;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.menu-sheet__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px 16px calc(24px + env(safe-area-inset-bottom, 0px));
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;
  touch-action: none;
  -webkit-overflow-scrolling: touch;
}

.menu-sheet__fish {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.menu-sheet__fish-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.menu-sheet__balance {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
  color: var(--color-text-normal);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);

  img {
    width: 36px;
    height: 36px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.menu-sheet__fish-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.menu-sheet__breakdown,
.menu-sheet__bonus {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.menu-sheet__breakdown {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
    border-radius: var(--radius-sm);
  }
}

.menu-sheet__meta-amount {
  font-weight: var(--font-weight-bold);
}

.menu-sheet__breakdown-chevron {
  display: block;
  width: 12px;
  height: 12px;
  font-size: 12px;
}

.menu-sheet__charge {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: var(--radius-rounded);
  background: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  ::v-deep .figma-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
